import { useEffect, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { apiBaseUrl } from '../../api/client'
import type { components } from '../../api/schema'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select'
import { useLabel } from './shared'

type PublicQuestionnaire = components['schemas']['PublicRentalQuestionnaire']
type PublicQuestion = components['schemas']['PublicRentalQuestion']
const UNSET = '__unset__'

function base(orgCode: string, token: string) {
    return `${apiBaseUrl}/c/${encodeURIComponent(orgCode)}/${encodeURIComponent(token)}/rental-questionnaire`
}

async function readError(res: Response): Promise<Error> {
    const body = (await res.json().catch(() => ({}))) as { title?: string; detail?: string }
    return new Error(body.detail ?? body.title ?? String(res.status))
}

/**
 * The applicant's questionnaire on the thread's public page (spec
 * 2026-10-25-11): the questions the manager's policies derive, nothing else.
 * Nothing renders when the thread carries no open application.
 */
export function PublicRentalQuestionnaire({ orgCode, token }: { orgCode: string; token: string }) {
    const { t } = useTranslation('rentalApplications')
    const label = useLabel()
    const queryClient = useQueryClient()
    const key = ['public-rental-questionnaire', orgCode, token]
    const { data } = useQuery({
        queryKey: key,
        retry: false,
        queryFn: async (): Promise<PublicQuestionnaire | null> => {
            const res = await fetch(base(orgCode, token))
            if (res.status === 404) return null
            if (!res.ok) throw await readError(res)
            return res.json()
        },
    })
    const [values, setValues] = useState<Record<string, unknown>>({})
    const [error, setError] = useState<string | null>(null)
    const [saved, setSaved] = useState(false)
    useEffect(() => {
        if (data) setValues(Object.fromEntries(data.questions.map((q) => [q.criterion_code, q.value])))
    }, [data])
    if (!data || data.questions.length === 0) return null

    const submit = async () => {
        setError(null)
        setSaved(false)
        const res = await fetch(base(orgCode, token), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ answers: values }),
        })
        if (!res.ok) {
            setError((await readError(res)).message)
            return
        }
        queryClient.setQueryData(key, await res.json())
        setSaved(true)
    }

    return (
        <section id="questionnaire" className="space-y-3 rounded-lg border bg-card p-4" data-testid="public-questionnaire">
            <h2 className="text-base font-semibold">{t('public.title')}</h2>
            <p className="text-sm text-muted-foreground">{t('public.description')}</p>
            {data.questions.map((q) => (
                <div key={q.criterion_code} className="space-y-1" data-testid="public-questionnaire-field" data-criterion={q.criterion_code}>
                    <label className="block text-sm font-medium">{label(q.label, q.criterion_code)}</label>
                    {q.description && <p className="text-xs text-muted-foreground">{label(q.description)}</p>}
                    <PublicInput q={q} value={values[q.criterion_code]} onChange={(v) => setValues({ ...values, [q.criterion_code]: v })} />
                    {q.evidence_document_codes.length > 0 && (
                        <PublicUpload orgCode={orgCode} token={token} q={q} onDone={(next) => queryClient.setQueryData(key, next)} />
                    )}
                </div>
            ))}
            {error && <p className="text-sm text-destructive" data-testid="public-questionnaire-error">{error}</p>}
            {saved && <p className="text-sm text-success" data-testid="public-questionnaire-saved">{t('public.saved')}</p>}
            <Button onClick={submit} data-testid="public-questionnaire-submit">{t('public.submit')}</Button>
        </section>
    )
}

function PublicInput({ q, value, onChange }: { q: PublicQuestion; value: unknown; onChange: (v: unknown) => void }) {
    const { t } = useTranslation('rentalApplications')
    if (q.value_type === 'boolean') {
        return (
            <Select value={value === true ? 'true' : value === false ? 'false' : UNSET} onValueChange={(v) => onChange(v === UNSET ? null : v === 'true')}>
                <SelectTrigger data-testid="public-questionnaire-input"><SelectValue /></SelectTrigger>
                <SelectContent>
                    <SelectItem value={UNSET}>{t('answers.unanswered')}</SelectItem>
                    <SelectItem value="true">{t('answers.yes')}</SelectItem>
                    <SelectItem value="false">{t('answers.no')}</SelectItem>
                </SelectContent>
            </Select>
        )
    }
    if (q.value_type === 'enum') {
        return (
            <Select value={typeof value === 'string' ? value : UNSET} onValueChange={(v) => onChange(v === UNSET ? null : v)}>
                <SelectTrigger data-testid="public-questionnaire-input"><SelectValue /></SelectTrigger>
                <SelectContent>
                    <SelectItem value={UNSET}>{t('answers.unanswered')}</SelectItem>
                    {(q.enum_values ?? []).map((v) => <SelectItem key={v} value={v}>{t(`enum.${v}`, { defaultValue: v })}</SelectItem>)}
                </SelectContent>
            </Select>
        )
    }
    if (q.value_type === 'date_range') {
        const range = (value ?? {}) as { from?: string; to?: string }
        return (
            <div className="flex gap-2" data-testid="public-questionnaire-input">
                <Input type="date" value={range.from ?? ''} onChange={(e) => onChange({ ...range, from: e.target.value })} />
                <Input type="date" value={range.to ?? ''} onChange={(e) => onChange({ ...range, to: e.target.value })} />
            </div>
        )
    }
    return (
        <Input
            type="number"
            min={0}
            value={typeof value === 'number' ? String(value) : ''}
            placeholder={q.value_type === 'ratio' ? t('answers.monthly_income') : ''}
            onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
            data-testid="public-questionnaire-input"
        />
    )
}

function PublicUpload({ orgCode, token, q, onDone }: { orgCode: string; token: string; q: PublicQuestion; onDone: (next: PublicQuestionnaire) => void }) {
    const { t } = useTranslation('rentalApplications')
    const [type, setType] = useState(q.evidence_document_codes[0] ?? '')
    const [error, setError] = useState<string | null>(null)
    const [sending, setSending] = useState(false)
    const send = async (file: File) => {
        setSending(true)
        setError(null)
        const form = new FormData()
        form.append('criterion_code', q.criterion_code)
        form.append('document_type_code', type)
        form.append('file', file)
        const res = await fetch(base(orgCode, token), { method: 'POST', body: form })
        setSending(false)
        if (!res.ok) {
            setError((await readError(res)).message)
            return
        }
        onDone(await res.json())
    }
    return (
        <div className="flex flex-wrap items-center gap-2 text-sm" data-testid="public-questionnaire-upload">
            {q.evidence_document_codes.length > 1 && (
                <Select value={type} onValueChange={setType}>
                    <SelectTrigger className="h-8 w-44" data-testid="public-questionnaire-upload-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        {q.evidence_document_codes.map((c) => <SelectItem key={c} value={c}>{t(`document_type.${c}`, { defaultValue: c })}</SelectItem>)}
                    </SelectContent>
                </Select>
            )}
            <input
                type="file"
                disabled={sending}
                onChange={(e) => e.target.files?.[0] && send(e.target.files[0])}
                data-testid="public-questionnaire-upload-input"
            />
            <span className="text-muted-foreground" data-testid="public-questionnaire-upload-count">{t('public.documents', { count: q.documents_count })}</span>
            {error && <span className="text-destructive" data-testid="public-questionnaire-upload-error">{error}</span>}
        </div>
    )
}
